import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Rocket, LogOut, Sparkles, Gift, BellDot, ChevronDown, Star } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const linkCls = ({ isActive }) =>
    `px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${isActive ? "bg-black text-white" : "text-gray-700 hover:text-black"}`;

  return (
    <header className="sticky top-0 z-40 bg-white border-b-2 border-black">
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-[#4338ca] border-2 border-black flex items-center justify-center shadow-[2px_2px_0_0_#0A0A0A]">
            <Rocket className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-black tracking-tight text-xl">Career AI Hub</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <NavLink to="/jobs" data-testid="nav-jobs" className={linkCls}>Jobs</NavLink>
          <NavLink to="/tailor" data-testid="nav-tailor" className={linkCls}>Tailor</NavLink>
          <NavLink to="/cover-letter" data-testid="nav-cover" className={linkCls}>Cover</NavLink>
          <NavLink to="/interview-prep" data-testid="nav-prep" className={linkCls}>Prep</NavLink>
          <NavLink to="/thank-you" data-testid="nav-thanks" className={linkCls}>Thanks</NavLink>
          <NavLink to="/tracker" data-testid="nav-tracker" className={linkCls}>Tracker</NavLink>
          <NavLink to="/pricing" data-testid="nav-pricing" className={linkCls}>Pricing</NavLink>
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <>
              {user.plan === "pro" ? (
                <span data-testid="pro-badge" className="nb-pill bg-[#eab308]">
                  <Sparkles className="w-3.5 h-3.5 mr-1" /> PRO
                </span>
              ) : (
                <Link to="/pricing" data-testid="upgrade-link" className="hidden sm:inline nb-pill bg-[#ec4899] text-white">Upgrade</Link>
              )}
              <div className="relative">
                <button data-testid="user-menu" onClick={() => setMenuOpen((v) => !v)}
                  className="hidden sm:flex items-center gap-1 pl-2 pr-1 py-1 rounded-full hover:bg-gray-100">
                  <div className="w-8 h-8 rounded-full bg-[#4338ca] text-white text-xs font-bold border-2 border-black flex items-center justify-center">
                    {(user.name || user.email).slice(0,1).toUpperCase()}
                  </div>
                  <ChevronDown className="w-4 h-4" />
                </button>
                {menuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                    <div data-testid="user-menu-dropdown" className="absolute right-0 mt-1 w-56 bg-white border-2 border-black rounded-xl shadow-[4px_4px_0_0_#0A0A0A] py-1 z-50">
                      <Link to="/alerts" data-testid="menu-alerts" onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-bold hover:bg-gray-100"><BellDot className="w-4 h-4" /> Job alerts</Link>
                      <Link to="/watchlist" data-testid="menu-watchlist" onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-bold hover:bg-gray-100"><Star className="w-4 h-4" /> Company watchlist</Link>
                      <Link to="/referrals" data-testid="menu-referrals" onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-bold hover:bg-gray-100"><Gift className="w-4 h-4" /> Refer & earn</Link>
                      <div className="border-t-2 border-black my-1" />
                      <button onClick={() => { logout(); setMenuOpen(false); nav("/"); }}
                        className="w-full text-left flex items-center gap-2 px-4 py-2 text-sm font-bold hover:bg-gray-100"><LogOut className="w-4 h-4" /> Log out</button>
                    </div>
                  </>
                )}
              </div>
              <button
                data-testid="logout-btn"
                onClick={() => { logout(); nav("/"); }}
                className="sm:hidden nb-btn bg-white px-3 py-1.5 text-sm"
                aria-label="Log out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="nb-btn bg-white px-4 py-1.5 text-sm">Log in</Link>
              <Link to="/signup" data-testid="nav-signup" className="nb-btn bg-[#4338ca] text-white px-4 py-1.5 text-sm">Sign up</Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
